import {
  Box,
  Card,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Menu,
  MenuItem,
  IconButton,
  TableContainer,
  Pagination,
} from "@mui/material";
import React, { useEffect, useState, useCallback } from "react";
import { AuthorizedLayout } from "../Layouts/AuthorizedLayout";
import { useNavigate } from "react-router-dom";
import ActionIcon from "../../Assets/images/png/Action.png";
import SearchandExport from "../../Components/Common/SearchandExport/SearchandExport";
import BoxHeader from "../../Components/Common/Box/Boxheader";
import { DeleteApiMethod, GetApiMethod } from "../../Utils/ApiService";

interface Booking {
  id: string;
  bookingNo: number;
  customerName: string;
  eventDate: string;
  eventTime: string;
  services: string;
  category: string;
  provider: string;
  status: string;
  eventLocation: string;
  bookingServices: Array<{
    id: string;
    name: string;
    type: string;
    status: number;
    createdAt: string;
    updatedAt: string;
  }>;
}


const Bookings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBookingId, setSelectedBookingId] = useState<string | null>(null);
  const navigate = useNavigate();

  // Pagination states
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 5;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = bookings.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(bookings.length / itemsPerPage);

 const isMounted = React.useRef(false);
  const [filteredBookings, setFilteredBookings] = useState<Booking[]>([]);
  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);
      const endpoint = `booking/list?page=1&limit=100&sort=createdAt&sortDirection=ASC`;
      const response = await GetApiMethod(endpoint);

      if (response?.status === 200 && response?.data?.data?.records) {
        const records = response.data.data.records.map((item: any) => ({
          id: item.id,
          bookingNo: item.bookingNo,
          customerName: item.bookingCustomer?.name || "N/A",
          eventDate: item.eventDate,
          eventTime: item.eventTime,
          category: item.bookingCategory?.name || "N/A",
          provider: item.bookingProvider?.providerName || "N/A",
          status: item.status,
          eventLocation: item.eventLocation,
          // Map the services field to a comma-separated list of service names
          services: item.bookingServices?.map((service: any) => service.name).join(", ") || "No services",
          bookingServices: item.bookingServices || [], // Keep the full services data for further use
        }));

        setBookings(records);
        setFilteredBookings(records); // keep a filtered copy
      } else {
        setBookings([]);
        setFilteredBookings([]);
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
      setBookings([]);
      setFilteredBookings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredBookings(bookings);
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = bookings.filter(
        (b) =>
          b.customerName.toLowerCase().includes(query) ||
          b.category.toLowerCase().includes(query) ||
          b.provider.toLowerCase().includes(query) ||
          b.bookingNo.toString().includes(query) ||
          b.eventLocation.toLowerCase().includes(query)
      );
      setFilteredBookings(filtered);
    }
    setCurrentPage(1); // reset to first page
  }, [searchQuery, bookings]);


  useEffect(() => {
    if (!isMounted.current)
{    fetchBookings();
   isMounted.current = true;
}
  }, [fetchBookings]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedBookingId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedBookingId(null);
  };
  const handleDeleteBooking = async (bookingId: string) => {
    try {
      const res = await DeleteApiMethod(`booking/${bookingId}`);

      if (res.status === 200) {
        alert("Booking deleted successfully.");
        fetchBookings();
      } else {
        alert("Failed to delete booking.");
      }
    } catch (err) {
      console.error("Error deleting booking:", err);
      alert("An error occurred while deleting the booking.");
    }
  };
  const getStatusColor = (status: string) => {
    let dotColor = "#6c757d"; // Default color
    let textColor = "#6c757d"; // Default color

    switch (status?.toLowerCase()) {
      case "pending":
        dotColor = "#FFA500";
        textColor = "#FFA500"; // Same color for text
        break;
      case "accepted":
        dotColor = "#28a745";
        textColor = "#28a745"; // Same color for text
        break;
      case "rejected":
        dotColor = "#dc3545";
        textColor = "#dc3545"; // Same color for text
        break;
      default:
        dotColor = "#6c757d";
        textColor = "#6c757d"; // Default color for text
    }

    return { dotColor, textColor };
  };


  return (
    <AuthorizedLayout>
      <Box sx={{ px: 2, py: 3, fontFamily: "Poppins, Semibold" }}>
        {loading && (
          <Box className="loading-overlay">
            <CircularProgress size={50} thickness={5} />
          </Box>
        )}

        <Card className="card-layout">
          <BoxHeader dashboard_name="Bookings" tabs={["All Bookings"]} activeTab={0} />

          {/* Search Bar */}
          <SearchandExport
            searchPlaceholder="Search bookings"
            onSearchChange={(value: string) => {
              setSearchQuery(value);
              setCurrentPage(1);
            }}
          />

          {/* Responsive Table */}
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className="table-header">Booking ID</TableCell>
                  <TableCell className="table-header">Customer Name</TableCell>
                  <TableCell className="table-header">Event Date&Event Time</TableCell>
                  <TableCell className="table-header"></TableCell>
                  <TableCell className="table-header">Category</TableCell>
                  <TableCell className="table-header">Services</TableCell>
                  <TableCell className="table-header">Provider</TableCell>

                  <TableCell className="table-header">Status</TableCell>
                  <TableCell className="table-header">Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {currentItems.length > 0 ? (
                  currentItems.map((booking) => {
                    const { dotColor, textColor } = getStatusColor(booking.status); // Get colors
                    return (
                      <TableRow key={booking.id}>
                        <TableCell className="table-cell">{booking.bookingNo}</TableCell>
                        <TableCell className="table-cell">{booking.customerName}</TableCell>
                        <TableCell className="table-cell">{booking.eventDate}{booking.eventTime}</TableCell>
                        <TableCell className="table-cell"></TableCell>
                        <TableCell className="table-cell">{booking.category}</TableCell>
                        <TableCell className="table-cell">{booking.services}</TableCell>
                        <TableCell className="table-cell">{booking.provider}</TableCell>
                        <TableCell className="table-cell">
                          <Box display="flex" alignItems="center">
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: dotColor, // Apply dot color
                                mr: 1,
                              }}
                            />
                            <Typography sx={{ color: textColor }}> {/* Apply text color */}
                              {booking.status}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell className="table-cell">
                          <Box display="flex" alignItems="center">
                            <Typography className="table-cell action">Action</Typography>
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, booking.id)}
                            >
                              <img src={ActionIcon} alt="action menu" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No bookings found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>

            </Table>
          </TableContainer>

          {/* Pagination same as Subcategory */}
          <Box mt={2} display="flex" justifyContent="center">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, page) => setCurrentPage(page)}
              color="primary"
              siblingCount={1}
              boundaryCount={0}
              showFirstButton={false}
              showLastButton={false}
            />
          </Box>
        </Card>

        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem
            onClick={() => {
              handleMenuClose();
              if (selectedBookingId) navigate(`/bookings/${selectedBookingId}`);
            }} className="view-menu-item"
          >
            View
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleMenuClose(); // Optional: close the menu first
              if (selectedBookingId) {
                handleDeleteBooking(selectedBookingId);
                console.log("Delete booking:", selectedBookingId);
              } else {
                console.warn("No booking selected for deletion.");
              }
            }}
            className="delete-menu-item"
          >
            Delete
          </MenuItem>

        </Menu>
      </Box>
    </AuthorizedLayout>
  );
};

export default Bookings;
